import React, { useState, useEffect } from "react";
import Header from "../../../Components/Header";
import Sidebar from "../../../Components/Sidebar";
import { Link, useNavigate, useParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { db } from "../../../config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { uploadFile, getFileURL, deleteFileFromStorage } from "../../../firebase/storageService";
import ImageUpload from "../../../Components/ImageUpload";

const EditService = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [imagePreview, setImagePreview] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentImagePath, setCurrentImagePath] = useState('');

    useEffect(() => {
        const fetchDocumentData = async () => {
            try {
                const docRef = doc(db, "partners", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setName(data.name);
                    setCurrentImagePath(data.imageUrl || '');
                    
                    // Load existing image if present
                    if (data.imageUrl) {
                        try {
                            const url = await getFileURL(data.imageUrl);
                            setImagePreview(url);
                        } catch (error) {
                            console.warn('Error loading image:', error);
                        }
                    }
                } else {
                    console.error('Document does not exist');
                    toast.error("Partner not found");
                    navigate("/serviceslist");
                }
            } catch (error) {
                console.error('Error fetching document data:', error);
                toast.error("Error fetching partner: " + error.message);
            }
        };

        fetchDocumentData();
    }, [id, navigate]);

    const handleImageLoad = (event, id) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            setNameError('Name is required');
            toast.error("Name is required", { autoClose: 2000 });
            return;
        }

        setLoading(true);
        try {
            let newImagePath = currentImagePath;
            
            // Upload new image if selected
            if (selectedFile) {
                const toastId = toast.loading("Uploading partner logo...");
                try {
                    const imagePath = `partners/${Date.now()}-${selectedFile.name}`;
                    await uploadFile(selectedFile, imagePath);
                    newImagePath = imagePath;
                    
                    // Delete old image if it exists and is different
                    if (currentImagePath && currentImagePath !== newImagePath) {
                        try {
                            await deleteFileFromStorage(currentImagePath);
                        } catch (error) {
                            console.warn('Error deleting old image:', error);
                        }
                    }
                    
                    toast.update(toastId, { 
                        render: "Logo uploaded successfully!", 
                        type: "success", 
                        isLoading: false, 
                        autoClose: 2000 
                    });
                } catch (error) {
                    toast.update(toastId, { 
                        render: "Logo upload failed: " + error.message, 
                        type: "error", 
                        isLoading: false, 
                        autoClose: 2000 
                    });
                    throw error;
                }
            }

            const docRef = doc(db, "partners", id);
            await updateDoc(docRef, {
                name: name,
                imageUrl: newImagePath,
                updatedAt: new Date()
            });
            sessionStorage.setItem('updatePartnerSuccess', 'true');
            navigate("/serviceslist");
        } catch (error) {
            toast.error("Error updating partner: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Header />
            <Sidebar
                id="menu-item4"
                id1="menu-items4"
                activeClassName="services"
            />
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item active">
                                        <Link to="/serviceslist">Partners</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">Edit Partner</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="form-heading">
                                                    <h4>Edit Partner</h4>
                                                </div>
                                            </div>
                                            {/* Name */}
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Partner Name <span className="login-danger">*</span></label>
                                                    <input
                                                        className={`form-control ${nameError ? 'is-invalid' : ''}`}
                                                        type="text"
                                                        value={name}
                                                        onChange={(e) => {
                                                            setName(e.target.value);
                                                            setNameError('');
                                                        }}
                                                        disabled={loading}
                                                    />
                                                    {nameError && <div className="invalid-feedback">{nameError}</div>}
                                                </div>
                                            </div>
                                            
                                            {/* Partner Logo Upload */}
                                            <div className="col-12">
                                                <div className="form-group local-forms">
                                                    <label>Partner Logo</label>
                                                    <div className="row">
                                                        <ImageUpload 
                                                            id="partner-logo" 
                                                            src={imagePreview} 
                                                            loadFile={handleImageLoad} 
                                                            imageName="Partner Logo" 
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Submit/Cancel Button */}
                                            <div className="col-12">
                                                <div className="form-action-group text-end">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary submit-form me-2"
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Updating Partner..." : "Update Partner"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary cancel-form"
                                                        onClick={() => navigate("/serviceslist")}
                                                        disabled={loading}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                    <ToastContainer />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditService;
